import type { Ref } from 'vue'
import { gql } from '@apollo/client/core'

import type { Player, RaidId, RaidPlayer, RaidStageId } from '../../types'
import { MAX_RAID_SNAPSHOT_BUILDS } from '../../lib/boardInvariants'

/**
 * `raidPlayerReplaceBuilds` swaps a snapshot row's entire `raid_player_builds`
 * set + its `active_build_index` in one write. The board's build selector
 * picks an existing personal or guild pair, which this slice fits into the
 * raid snapshot's six slots. Backend resolver:
 * `apps/backend/api/src/raid_mutations.rs::f_raid_player_replace_builds`.
 */
const RaidPlayerReplaceBuildsDocument = gql`
  mutation RaidPlayerReplaceBuilds(
    $guildId: Int!
    $raidId: Int!
    $raidPlayerId: Int!
    $builds: [String!]!
    $activeBuildIndex: Int!
    $stageId: Int
  ) {
    raidPlayerReplaceBuilds(
      guildId: $guildId
      raidId: $raidId
      raidPlayerId: $raidPlayerId
      builds: $builds
      activeBuildIndex: $activeBuildIndex
      stageId: $stageId
    )
  }
`

export interface RaidPlayerReplaceBuildsMutation {
  readonly raidPlayerReplaceBuilds: boolean
}

type ReportingRunner = <T>(
  mutation: unknown,
  variables: Record<string, unknown>,
  acknowledge?: (result: T) => boolean,
) => Promise<boolean>

export interface RaidPlayerBuildsActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  /**
   * Live-board players, indexed by `discordUserId`. Used to seed a raid
   * snapshot row's `builds` from the player's library the first time
   * `setActiveBuild` runs against a snapshot row that has no per-snapshot
   * builds yet — mirrors the seed-on-create behaviour of the backend's
   * `snapshot_into` so the FX-05 chip lights up on every raid card the
   * player has ever saved a build for, even when no one has manually
   * promoted a build into the raid yet.
   */
  readonly livePlayersByDiscordId: Ref<ReadonlyMap<string, Player>>
  readonly error: Ref<string | null>
  readonly runReporting: ReportingRunner
}

export interface RaidPlayerBuildsActionsApi {
  /**
   * Switch which saved build is active for a raid snapshot row. Preserve
   * existing snapshot pairs where they fit and include the selected pair.
   */
  readonly setActiveBuild: (id: string, index: number) => void
  /** Select a complete pair from a source outside the personal library, such as a guild build. */
  readonly setActiveBuildPair: (
    id: string,
    weapons: ReadonlyArray<string>,
  ) => Promise<boolean>
}

/**
 * A snapshot build row must carry exactly two distinct, non-empty weapon slugs
 * — mirrors the backend's `snapshot_into` + `replace_raid_player_builds`
 * invariant. This also guards against malformed legacy snapshot rows.
 */
function isBuild(weapons: ReadonlyArray<string>): boolean {
  return weapons.length === 2
    && weapons[0] !== undefined
    && weapons[1] !== undefined
    && weapons[0].trim() !== ''
    && weapons[1].trim() !== ''
    && weapons[0] !== weapons[1]
}

function sameBuild(first: ReadonlyArray<string>, second: ReadonlyArray<string>): boolean {
  return first.length === second.length && first.every((slug, index) => slug === second[index])
}

/** Keep the snapshot history while making the selected live-library pair available to it. */
function mergeBuilds(
  ...sources: ReadonlyArray<ReadonlyArray<ReadonlyArray<string>>>
): ReadonlyArray<ReadonlyArray<string>> {
  let merged: ReadonlyArray<ReadonlyArray<string>> = []
  for (const source of sources) {
    for (const build of source) {
      if (!merged.some((candidate) => sameBuild(candidate, build))) merged = [...merged, build]
    }
  }
  return merged
}

/** Keep the earliest pairs, replacing the active slot when all six are full. */
export function snapshotBuildsForSelection(
  snapshotBuilds: ReadonlyArray<ReadonlyArray<string>>,
  libraryBuilds: ReadonlyArray<ReadonlyArray<string>>,
  weapons: ReadonlyArray<string>,
  activeBuildIndex: number,
): ReadonlyArray<ReadonlyArray<string>> {
  const merged = mergeBuilds(snapshotBuilds, [weapons], libraryBuilds)
  const selectedIndex = merged.findIndex((build) => sameBuild(build, weapons))
  const kept = merged.slice(0, MAX_RAID_SNAPSHOT_BUILDS)
  if (selectedIndex < MAX_RAID_SNAPSHOT_BUILDS) return kept

  const replacementIndex = activeBuildIndex >= 0 && activeBuildIndex < kept.length
    ? activeBuildIndex
    : kept.length - 1
  return kept.map((build, index) => index === replacementIndex ? weapons : build)
}

export function useRaidPlayerBuildsActions(
  ctx: RaidPlayerBuildsActionsCtx,
): RaidPlayerBuildsActionsApi {
  async function setActiveBuildPair(
    id: string,
    weapons: ReadonlyArray<string>,
  ): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    const isCurrentRaid = (): boolean => ctx.activeRaidId.value === raidId
    const player = ctx.raidPlayers.value.find((candidate) => candidate.id === id)
    if (raidId === null || player === undefined) {
      ctx.error.value = 'The selected raid player is no longer available.'
      return false
    }
    if (!isBuild(weapons)) {
      ctx.error.value = 'Each build requires two distinct weapons.'
      return false
    }
    // The raid owns the active choice. Keep its existing build rows first,
    // add the selected pair, then fill free slots from the personal library.
    // Replace the previously active slot only when all six are already full.
    const libraryBuilds = ctx.livePlayersByDiscordId.value.get(player.discordUserId)?.builds ?? []
    const submittedBuilds = snapshotBuildsForSelection(
      player.builds,
      libraryBuilds,
      weapons,
      player.activeBuildIndex,
    )
    if (submittedBuilds.some((build) => !isBuild(build))) {
      ctx.error.value = 'Each build requires two distinct weapons.'
      return false
    }
    const activeBuildIndex = submittedBuilds.findIndex((build) => sameBuild(build, weapons))
    if (activeBuildIndex < 0) {
      ctx.error.value = 'That build is no longer available.'
      return false
    }
    const succeeded = await ctx.runReporting<RaidPlayerReplaceBuildsMutation>(
      RaidPlayerReplaceBuildsDocument,
      {
        guildId: ctx.activeDiscordGuildId.value,
        raidId: Number(raidId),
        raidPlayerId: Number(player.id),
        builds: submittedBuilds.map((build) => JSON.stringify(build)),
        activeBuildIndex,
        stageId: ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
      },
      (result) => result.raidPlayerReplaceBuilds,
    )
    return succeeded && isCurrentRaid()
  }

  function setActiveBuild(id: string, index: number): void {
    const player = ctx.raidPlayers.value.find((candidate) => candidate.id === id)
    if (player === undefined) {
      ctx.error.value = 'The selected raid player is no longer available.'
      return
    }
    // Cards render the current personal library when it is available, so the
    // clicked index must be resolved against that same source rather than the
    // raid's internal merged list.
    const libraryBuilds = ctx.livePlayersByDiscordId.value.get(player.discordUserId)?.builds ?? player.builds
    const selectedBuild = libraryBuilds[index]
    if (selectedBuild === undefined) {
      ctx.error.value = 'That build is no longer available.'
      return
    }
    void setActiveBuildPair(id, selectedBuild)
  }

  return { setActiveBuild, setActiveBuildPair }
}
